import { Transition } from '@headlessui/react';
import { FC, ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, PlusIcon } from '../../../assets/icons/index';
import Button from '../../../components/button/index';
import { FilterRadioGroup } from '../../../components/filter/index';
import { PaginationController } from '../../../components/pagination-controller/index';
import RootLoader from '../../../components/root-loader/index';
import { Search } from '../../../components/search/index';
import {
  Select,
  SelectButton,
  SelectOption,
  SelectOptions,
} from '../../../components/select/index';
import { usePagination } from '../../../hooks/index';
import { StatusFilter } from '../../../types/general.types';
import { GeneralEmptyState } from './empty-state/general';
import { SearchEmptyState } from './empty-state/search';
import { formStore } from './form/form.store';
import { PerkFormModal } from './form/form-modal';
import { PerkSortByPayload, usePerks } from './hooks/use-perks.hook';
import { PerkClaimType, perkClaimTypeOrderMap, perkClaimTypeTranslationKey } from './perks.types';
import { popupStore } from './popup/popup.store';
import { popupTypeComponent } from './popup/popup.types';
import {
  perksStatusFilterMap,
  perksStatusFilterTranslationKey,
  PerkStatusFilterUnion,
} from './table.types';
import { PerksRow } from './table/perks-row';
import { PerksTable } from './table/perks-table';

const PerksWrapper: FC<{ children: ReactNode; isLoading: boolean }> = ({ children, isLoading }) => {
  const popupState = popupStore(({ open }) => open);

  const popupType = popupStore(({ type }) => type);
  return (
    <>
      {isLoading && <RootLoader />}
      {popupState && popupTypeComponent[popupType]}
      <PerkFormModal />
      {children}
    </>
  );
};

export const Perks: FC = () => {
  const [sort, setSort] = useState<PerkSortByPayload>({
    field: null,
    order: null,
  });

  const [filter, setFilter] = useState<PerkStatusFilterUnion>(StatusFilter.ALL);

  const [typeFilter, setTypeFilter] = useState<null | PerkClaimType>(null);

  const [search, setSearch] = useState('');

  const [currentPage, setCurrentPage, take, updateTake, takeOptions, resetCounter] =
    usePagination();

  const openCreate = formStore(({ openCreate }) => openCreate);

  const handleTypeFilter = (type: null | PerkClaimType) => {
    setTypeFilter(type);
    resetCounter();
  };

  const handleFilter = (type: PerkStatusFilterUnion) => {
    setFilter(type);
    resetCounter();
  };

  const {
    isLoading,
    records: perks,
    totalRecords,
    totalPages,
    isFetched,
  } = usePerks({
    filter,
    search,
    skip: take * (currentPage - 1),
    sort,
    take,
    type: typeFilter || undefined,
  });

  const isDefaultFilters = filter === StatusFilter.ALL && !typeFilter && !search;

  if (!totalRecords && isDefaultFilters && isFetched) {
    return (
      <PerksWrapper isLoading={isLoading}>
        <GeneralEmptyState />
      </PerksWrapper>
    );
  }

  const recordIds = perks.map(({ id }) => id);

  return (
    <PerksWrapper isLoading={isLoading}>
      <div className="flex h-full w-full flex-col gap-4">
        <div className="flex items-center justify-between">
          <FilterRadioGroup
            callback={handleFilter}
            translation={perksStatusFilterTranslationKey}
            value={filter}
            variants={perksStatusFilterMap}
          />

          <div className="flex items-center gap-3">
            <Search
              callback={(v) => {
                setSearch(v);
              }}
            />
            <Select onChange={handleTypeFilter as () => void} value={typeFilter as string}>
              {({ open }) => (
                <>
                  <div className="relative rounded border-gray-100">
                    <SelectButton className="flex justify-between border-gray-100 bg-gray-50 p-3 px-[6px]">
                      <span className="text-sm font-[450] text-[#344054]">
                        Type: {typeFilter ? perkClaimTypeTranslationKey[typeFilter] : 'All'}
                      </span>
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    </SelectButton>

                    <Transition
                      as="div"
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                      show={open}
                    >
                      <SelectOptions>
                        <SelectOption value={null as unknown as string}>
                          {({ selected }) => (
                            <span
                              className={twMerge(
                                selected ? 'font-[450]' : 'font-normal',
                                'block text-sm text-gray-700',
                              )}
                            >
                              All
                            </span>
                          )}
                        </SelectOption>
                        {perkClaimTypeOrderMap.map((filter) => (
                          <SelectOption key={filter} value={filter}>
                            {({ selected }) => (
                              <span
                                className={twMerge(
                                  selected ? 'font-[450]' : 'font-normal',
                                  'block text-nowrap text-sm text-gray-700',
                                )}
                              >
                                {perkClaimTypeTranslationKey[filter]}
                              </span>
                            )}
                          </SelectOption>
                        ))}
                      </SelectOptions>
                    </Transition>
                  </div>
                </>
              )}
            </Select>
            <Button
              className="flex h-9 w-fit shrink-0 flex-nowrap gap-1 text-nowrap px-4"
              onClick={openCreate}
            >
              <PlusIcon />
              <span>Create Perk</span>
            </Button>
          </div>
        </div>

        {isFetched && (
          <div className="flex h-full gap-2 overflow-hidden rounded-md">
            {perks.length ? (
              <div className="flex h-full w-full flex-col justify-between gap-3 overflow-hidden">
                <div className="h-fit w-full overflow-x-auto rounded-md border border-gray-100">
                  <PerksTable handleSort={setSort} recordIds={recordIds}>
                    {perks.map((data) => (
                      <PerksRow data={data} key={data.id} />
                    ))}
                  </PerksTable>
                </div>
                <PaginationController
                  currentPage={currentPage}
                  itemsToFetch={take}
                  itemsToFetchMap={takeOptions}
                  onClick={setCurrentPage}
                  onItemToFetchChange={updateTake}
                  totalPages={totalPages}
                />
              </div>
            ) : (
              <SearchEmptyState filter={filter} search={search} />
            )}
          </div>
        )}
      </div>
    </PerksWrapper>
  );
};
